import type { Team } from '../../schemas/tournaments.js'
import { getCupsForPhase } from '../../tournament/cups.js'
import { loserOf, progressAfter, winnerOf, type ProgressMatch, type StandingRules } from '../../tournament/progress.js'
import { seededRng, type Rng } from '../../tournament/random.js'
import { calculateStandings } from '../../tournament/standings.js'
import { matchFacts, tournamentHistory, type MatchFacts } from './history.js'
import type { Outcome, OutrightMarketKey, Resolution } from './markets.js'
import type { MatchParams } from './params.js'
import { priceOutcome } from './price.js'
import type { QuotedMarket, QuotedOutcome } from './quote.js'
import { playOnce } from './simulate.js'

/** Il torneo come lo vedono gli antepost: squadre, partite reali in ordine di turno e i loro fatti. */
export type OutrightBoard = {
  settings: StandingRules & { cupsPerTeamGroup: number; cupsPerTeamFinal: number }
  /** Nell'ordine del database (a parità piena la classifica lo conserva, come nel backend); giocatori in ordine stabile. */
  teams: readonly { id: string; players: readonly string[] }[]
  /** In ordine di turno e posizione (round, sortOrder). */
  matches: readonly (ProgressMatch & { id: string })[]
  /** Fatti (eventi rigiocati) delle partite reali; una partita senza fatti non ha ancora bicchieri. */
  facts: ReadonlyMap<string, MatchFacts>
}

export type OutrightFacts = {
  /**
   * Antepost chiusi: esiste una partita a eliminazione, cioè il girone è finito e il tabellone è generato (anche con
   * tutte le partite ancora da giocare). Un torneo da 2 squadre parte già dalla finale, senza girone: chiuso dall'inizio.
   */
  closed: boolean
  /** Le due squadre della finale, appena la finale esiste. */
  finalists: readonly string[] | null
  /** Vincitrice, a finale conclusa. */
  winner: string | null
  /** Torneo finito: ogni partita conclusa, finale compresa. */
  completed: boolean
  /** Fuori dalla corsa al titolo: fuori dal tabellone dopo il girone, o sconfitte in una partita a eliminazione. */
  eliminated: ReadonlySet<string>
  /** Senza più partite. Chi perde una semifinale gioca ancora la finalina, quindi non c'è. */
  done: ReadonlySet<string>
  /** Bicchieri di ogni giocatore nel torneo (tiri del pareggio compresi), e nelle sole partite concluse. */
  cups: ReadonlyMap<string, number>
  completedCups: ReadonlyMap<string, number>
}

const addCups = (tally: Map<string, number>, cups: Record<string, number>) => {
  for (const [id, n] of Object.entries(cups)) tally.set(id, (tally.get(id) ?? 0) + n)
}

/** Lo stato del torneo che decide gli antepost, dalle partite reali. */
export function outrightFacts({ teams, matches, facts }: OutrightBoard): OutrightFacts {
  const knockout = matches.filter((m) => m.phase !== 'group')
  const played = knockout.filter((m) => m.status === 'completed')
  const inKnockout = new Set(knockout.flatMap((m) => [m.homeTeamId, m.awayTeamId]))
  // Fuori dal tabellone solo quando il tabellone c'è: durante il girone nessuno è ancora eliminato.
  const notQualified = knockout.length > 0 ? teams.map((t) => t.id).filter((id) => !inKnockout.has(id)) : []
  const final = matches.find((m) => m.phase === 'final')
  const cups = new Map<string, number>()
  const completedCups = new Map<string, number>()
  for (const m of matches) {
    const f = facts.get(m.id)
    if (!f) continue
    for (const tally of m.status === 'completed' ? [cups, completedCups] : [cups]) {
      addCups(tally, f.homePlayerCups)
      addCups(tally, f.awayPlayerCups)
    }
  }
  return {
    closed: knockout.length > 0,
    finalists: final ? [final.homeTeamId, final.awayTeamId] : null,
    winner: final?.status === 'completed' ? winnerOf(final) : null,
    completed: final !== undefined && matches.every((m) => m.status === 'completed'),
    eliminated: new Set([...notQualified, ...played.map(loserOf)]),
    done: new Set([
      ...notQualified,
      ...played.filter((m) => m.phase !== 'semifinal').map(loserOf),
      ...played.filter((m) => m.phase === 'third_place').map(winnerOf),
    ]),
    cups,
    completedCups,
  }
}

export type OutrightResolution = { status: Resolution; deadHeat: number }
const OPEN: OutrightResolution = { status: 'open', deadHeat: 1 }
const LOST: OutrightResolution = { status: 'lost', deadHeat: 1 }
const decided = (won: boolean): OutrightResolution => (won ? { status: 'won', deadHeat: 1 } : LOST)

/**
 * Stato di un esito antepost (spec 4): la stessa regola per la quotazione e la liquidazione.
 * - Vincitore: a finale conclusa.
 * - Finalista: appena la finale esiste.
 * - Capocannoniere: a torneo finito, con il pari merito ("dead heat": `deadHeat` = quanti sono in testa).
 * Prima di allora l'esito è perso appena è deciso: squadra eliminata, oppure giocatore senza più partite già superato da
 * qualcuno (i bicchieri degli altri possono solo crescere). Decide solo con partite concluse, che non si riaprono, e
 * con la finale, che non si cancella: una decisione non va mai corretta.
 */
export function resolveOutright(board: OutrightBoard, facts: OutrightFacts, o: Outcome): OutrightResolution {
  const id = o.subjectId ?? o.selection
  switch (o.market) {
    case 'outright_winner':
      return facts.winner ? decided(facts.winner === id) : facts.eliminated.has(id) ? LOST : OPEN
    case 'outright_finalist':
      return facts.finalists ? decided(facts.finalists.includes(id)) : facts.eliminated.has(id) ? LOST : OPEN
    case 'outright_top_scorer': {
      const players = board.teams.flatMap((t) => t.players)
      if (facts.completed) {
        const max = Math.max(0, ...players.map((p) => facts.cups.get(p) ?? 0))
        const leaders = players.filter((p) => max > 0 && (facts.cups.get(p) ?? 0) === max)
        return leaders.includes(id) ? { status: 'won', deadHeat: leaders.length } : LOST
      }
      const team = board.teams.find((t) => t.players.includes(id))
      const mine = facts.completedCups.get(id) ?? 0
      return team && facts.done.has(team.id) && players.some((p) => (facts.completedCups.get(p) ?? 0) > mine) ? LOST : OPEN
    }
    default:
      return { status: 'void', deadHeat: 1 }
  }
}

/** Tornei simulati per stato (spec 4, `outrights.ts`). */
export const OUTRIGHT_RUNS = 2_000
/**
 * Parametri di una partita, anche non ancora creata: il backend li costruisce come per le quote delle partite, dallo
 * storico delle sole concluse. La fase conta solo per i bicchieri che dà: per questo `simulateTournament` li tiene in
 * cache per squadre e bicchieri, non per fase.
 */
export type ParamsFor = (homeTeamId: string, awayTeamId: string, phase: string) => MatchParams
/** Conteggi dei tornei simulati: vincitrice, finaliste (due per torneo), capocannoniere (1/k a ognuno dei k in testa). */
export type OutrightTally = { runs: number; winner: Record<string, number>; finalist: Record<string, number>; topScorer: Record<string, number> }
export const EMPTY_TALLY: OutrightTally = { runs: 0, winner: {}, finalist: {}, topScorer: {} }

const merge = (a: Record<string, number>, b: Record<string, number>): Record<string, number> =>
  Object.entries(b).reduce((acc, [id, n]) => ({ ...acc, [id]: (acc[id] ?? 0) + n }), a)
/** Somma di due simulazioni: il backend simula a pezzi per non fermare il processo. */
export const addTallies = (a: OutrightTally, b: OutrightTally): OutrightTally => ({
  runs: a.runs + b.runs, winner: merge(a.winner, b.winner), finalist: merge(a.finalist, b.finalist), topScorer: merge(a.topScorer, b.topScorer),
})

const EMPTY_FACTS = matchFacts({ homeTeamId: '', awayTeamId: '', cups: 0, events: [], airballs: { home: 0, away: 0 }, rearrangements: 0, seconds: 0 })

/** I bicchieri di una squadra fra i suoi giocatori: per ognuno un tiratore estratto con le quote nella coppia. */
function splitCups(n: number, players: readonly string[], shares: readonly number[], cups: Map<string, number>, rng: Rng): void {
  if (players.length === 0) return
  for (let k = 0; k < n; k++) {
    const x = rng()
    let i = 0
    let acc = shares[0]
    while (x >= acc && i < shares.length - 1) acc += shares[++i]
    cups.set(players[i], (cups.get(players[i]) ?? 0) + 1)
  }
}

/**
 * Antepost (spec 4): gioca `runs` volte il resto del torneo.
 * - Ogni partita non conclusa si gioca con `playOnce`; quelle in corso ripartono dal loro punteggio.
 * - A fase finita il torneo avanza con `progressAfter`, la stessa regola del backend, fino alla finale.
 * Conta vincitrice, finaliste e capocannoniere (pari merito: 1/k a ognuno dei k in testa).
 * È deterministica: stesso tabellone e stesso seme → stessi conteggi.
 */
export function simulateTournament(board: OutrightBoard, paramsFor: ParamsFor, { seed, runs = OUTRIGHT_RUNS }: { seed: number; runs?: number }): OutrightTally {
  const rng = seededRng(seed)
  const teamIds = board.teams.map((t) => t.id)
  const players = board.teams.flatMap((t) => t.players)
  const start = outrightFacts(board).cups
  const paramsCache = new Map<string, MatchParams>()
  // Airball e riposizionamenti non cambiano né chi vince né i bicchieri: spenti, ogni partita costa meno.
  const paramsOf = (m: ProgressMatch, cups: number): MatchParams => {
    const key = `${m.homeTeamId}|${m.awayTeamId}|${cups}`
    const hit = paramsCache.get(key)
    if (hit) return hit
    const params = { ...paramsFor(m.homeTeamId, m.awayTeamId, m.phase), airballsPerSecond: 0, rearrangementsPerSecond: 0 }
    paramsCache.set(key, params)
    return params
  }
  // Contatori locali, aggiornati sul posto: è il ciclo caldo del motore.
  const winner: Record<string, number> = {}
  const finalist: Record<string, number> = {}
  const topScorer: Record<string, number> = {}
  const bump = (tally: Record<string, number>, id: string, by = 1) => {
    tally[id] = (tally[id] ?? 0) + by
  }
  let played = 0
  for (let r = 0; r < runs; r++) {
    const list: (ProgressMatch & { id: string })[] = [...board.matches]
    const cups = new Map(start)
    let champion: string | null = null
    for (let i = 0; i < list.length; i++) {
      const m = list[i]
      if (m.status === 'completed') continue
      const maxCups = getCupsForPhase(m.phase, board.settings)
      const params = paramsOf(m, maxCups)
      const facts = board.facts.get(m.id) ?? EMPTY_FACTS
      const play = playOnce(params, facts, maxCups, rng)
      splitCups(play.homeScore - facts.homeScore, params.homePlayers, params.homeShares, cups, rng)
      splitCups(play.awayScore - facts.awayScore, params.awayPlayers, params.awayShares, cups, rng)
      list[i] = { ...m, homeScore: play.homeScore, awayScore: play.awayScore, timerElapsed: play.seconds, status: 'completed' }
      // Si avanza solo a fase finita. Prima `progressAfter` risponderebbe comunque 'none': stesso risultato, un terzo del tempo su 16 squadre.
      if (list.some((x, j) => j > i && x.phase === m.phase && x.status !== 'completed')) continue
      const step = progressAfter(teamIds, list, m.phase, board.settings)
      if (step.kind === 'advance') list.push(...step.insert.map((n) => ({ ...n, id: '', homeScore: null, awayScore: null, status: 'scheduled', timerElapsed: 0 })))
      else if (step.kind === 'finished') champion = step.winner
    }
    const final = list.find((m) => m.phase === 'final')
    // Senza campione (torneo già finito) il giro non conta: con gli antepost aperti c'è sempre una finale da giocare.
    if (champion === null || !final) continue
    played++
    bump(winner, champion)
    bump(finalist, final.homeTeamId)
    bump(finalist, final.awayTeamId)
    const max = Math.max(...players.map((id) => cups.get(id) ?? 0))
    const leaders = players.filter((id) => (cups.get(id) ?? 0) === max)
    for (const id of leaders) bump(topScorer, id, 1 / leaders.length)
  }
  return { runs: played, winner, finalist, topScorer }
}

/**
 * Le variabili che il simulatore vede di ogni squadra e giocatore (memoria "a parità di variabili, stessa quota").
 * - Squadra: storico del torneo (forza), riga di classifica del girone, turni a eliminazione raggiunti, partite che
 *   restano (fase, punteggio e bicchieri dal proprio lato, variabili dell'avversaria; casa o trasferta solo a
 *   eliminazione).
 * - Giocatore: la squadra, i bicchieri suoi e dei compagni.
 * Nel girone casa e trasferta non entrano: il vantaggio di una partita in casa in più vale meno del rumore di 2.000 tornei.
 */
// ponytail: simmetria al primo ordine. L'avversaria entra con le sue variabili, non con il suo calendario, e il cammino
// nel tabellone oltre la prossima partita non entra. Se due squadre "uguali" vanno separate, aggiungerlo alla chiave.
function signatures(board: OutrightBoard): { teams: Map<string, string>; players: Map<string, string> } {
  const completed = board.matches.filter((m) => m.status === 'completed')
  const history = tournamentHistory(completed.flatMap((m) => board.facts.get(m.id) ?? []))
  const stub: Team[] = board.teams.map((t) => ({ id: t.id, tournamentId: '', name: '', seed: null, players: [] }))
  const rows = new Map(calculateStandings(stub, completed, board.settings).map((s) => [s.team.id, s]))
  const base = new Map(
    board.teams.map((t) => {
      const h = history.teams.get(t.id)
      const s = rows.get(t.id)!
      return [t.id, [h?.made ?? 0, h?.conceded ?? 0, s.points, s.played, s.goalsFor, s.goalsAgainst, s.avgWinTime ?? '-'].join('/')] as const
    }),
  )
  const teams = new Map(
    board.teams.map((t) => {
      const own = board.matches.filter((m) => m.homeTeamId === t.id || m.awayTeamId === t.id)
      const pending = own
        .filter((m) => m.status !== 'completed')
        .map((m) => {
          const home = m.homeTeamId === t.id
          const f = board.facts.get(m.id)
          const state = !f ? [] : home ? [f.homeScore, f.awayScore, f.takenByHome, f.takenByAway, f.last, f.seconds] : [f.awayScore, f.homeScore, f.takenByAway, f.takenByHome, -f.last, f.seconds]
          const side = m.phase === 'group' ? '' : home ? 'h' : 'a'
          return [m.phase, side, base.get(home ? m.awayTeamId : m.homeTeamId), ...state].join(':')
        })
        .sort()
      const reached = own.filter((m) => m.phase !== 'group').map((m) => m.phase).join(',')
      return [t.id, `${base.get(t.id)}|${reached}|${pending.join(';')}`] as const
    }),
  )
  const cups = outrightFacts(board).cups
  const players = new Map(
    board.teams.flatMap((t) => {
      const playerCups = history.teams.get(t.id)?.playerCups ?? {}
      const mates = t.players.map((p) => playerCups[p] ?? 0).sort((a, b) => a - b).join(',')
      return t.players.map((p) => [p, `${teams.get(t.id)}|${mates}|${playerCups[p] ?? 0}|${cups.get(p) ?? 0}`] as const)
    }),
  )
  return { teams, players }
}

/** Media della probabilità fra soggetti con le stesse variabili: il rumore della simulazione non li separa. */
function symmetrize(ids: readonly string[], chance: (id: string) => number, variables: (id: string) => string): Map<string, number> {
  const groups = new Map<string, string[]>()
  for (const id of ids) groups.set(variables(id), [...(groups.get(variables(id)) ?? []), id])
  const out = new Map<string, number>()
  for (const members of groups.values()) {
    const mean = members.reduce((total, id) => total + chance(id), 0) / members.length
    for (const id of members) out.set(id, mean)
  }
  return out
}

type MarketInput = { key: OutrightMarketKey; ids: readonly string[]; chance: (id: string) => number; variables: (id: string) => string }

function quoteMarket(board: OutrightBoard, facts: OutrightFacts, margin: number, { key, ids, chance, variables }: MarketInput): QuotedMarket | null {
  const probability = symmetrize(ids, chance, variables)
  const outcomes = ids
    .flatMap((id): QuotedOutcome[] => {
      const outcome = { market: key, selection: id, subjectId: id }
      const { status } = resolveOutright(board, facts, outcome)
      // Eliminati (e capocannonieri già superati): nessun esito.
      if (status === 'lost' || status === 'void') return []
      // Deciso (finaliste appena la finale esiste, quindi ad antepost già chiusi): mai una quota, come in `quoteMatch`.
      if (status === 'won') return [{ ...outcome, odds: null, probability: 1, resolution: 'won' }]
      const p = probability.get(id) ?? 0
      const odds = facts.closed ? null : priceOutcome({ win: p, void: 0 }, margin)
      // Aperto ma senza prezzo (troppo improbabile): non si offre. Chiuso: resta, senza quota, per mostrarne lo stato.
      if (odds === null && !facts.closed) return []
      return [{ ...outcome, odds, probability: p, resolution: 'open' }]
    })
    .sort((a, b) => b.probability - a.probability)
  if (outcomes.length === 0) return null
  const status = outcomes.every((o) => o.resolution !== 'open') ? 'decided' : facts.closed ? 'closed' : 'open'
  return { key, group: 'outrights', status, outcomes }
}

/**
 * I tre mercati antepost: probabilità dai conteggi (`tally` null = antepost chiusi o torneo finito: niente quote), uguali
 * fra soggetti con le stesse variabili, prezzate come i mercati di partita. Le probabilità del finalista sommano a 2.
 */
export function quoteOutrights({ board, facts, tally, margin }: { board: OutrightBoard; facts: OutrightFacts; tally: OutrightTally | null; margin: number }): QuotedMarket[] {
  const sig = signatures(board)
  const of = (counts: Record<string, number> | undefined) => (id: string) => (tally && tally.runs > 0 ? (counts?.[id] ?? 0) / tally.runs : 0)
  const teamIds = board.teams.map((t) => t.id)
  const teamVariables = (id: string) => sig.teams.get(id) ?? id
  return [
    quoteMarket(board, facts, margin, { key: 'outright_winner', ids: teamIds, chance: of(tally?.winner), variables: teamVariables }),
    quoteMarket(board, facts, margin, { key: 'outright_finalist', ids: teamIds, chance: of(tally?.finalist), variables: teamVariables }),
    quoteMarket(board, facts, margin, { key: 'outright_top_scorer', ids: board.teams.flatMap((t) => t.players), chance: of(tally?.topScorer), variables: (id) => sig.players.get(id) ?? id }),
  ].filter((m): m is QuotedMarket => m !== null)
}
